import React, { useState, useCallback } from 'react';
import { Play, HelpCircle, Coins, TrendingUp, Loader2 } from 'lucide-react';
import { StockCategoryFilter, CategoryDisplayConfig } from '../../types/stockCategories';
import { cn } from '../../utils/cn';
import { PreviousDays } from './PreviousDays';


interface GameStartOverlayProps {
  category: StockCategoryFilter;
  categoryConfig: CategoryDisplayConfig;
  onPlay: () => void;
  isLoading?: boolean;
}

/**
 * Full-screen overlay that appears at the start of each game,
 * introducing the category before gameplay begins.
 * The timer only starts when the user clicks "Play".
 */
export const GameStartOverlay: React.FC<GameStartOverlayProps> = ({
  category,
  categoryConfig,
  onPlay,
  isLoading = false,
}) => {
  const [leaving, setLeaving] = useState(false);

  const handlePlay = useCallback(() => {
    setLeaving(true);
    setTimeout(() => {
      onPlay();
    }, 400);
  }, [onPlay]);

  return (
    <div
      className={cn(
        "min-h-[calc(100vh-8rem)] flex flex-col items-center px-6 pt-6 pb-6 bg-background text-foreground",
        leaving && "animate-fade-out-down"
      )}
      style={{
        width: '100vw',
        marginLeft: 'calc(-50vw + 50%)',
        animation: 'bgFadeIn 0.5s ease-out forwards',
      }}
    >
      <div className="max-w-md w-full text-center space-y-2">
        {/* Heading */}
        <h1
          className="text-xl sm:text-2xl font-bold uppercase tracking-wide animate-fade-up opacity-0 text-foreground/90"
        >
          Guess the {categoryConfig.label}
          <br />
          Stock of the Day
        </h1>

        {/* Category Icon + Name (centered), with avatar absolutely positioned */}
        <div className="relative w-fit mx-auto space-y-2 animate-fade-up-delay opacity-0">
          {/* Category Icon */}
          <div className="text-5xl sm:text-6xl drop-shadow-lg" role="img" aria-label={categoryConfig.label}>
            {categoryConfig.icon}
          </div>

          {/* Category Name */}
          <h2
            className="text-2xl sm:text-3xl font-extrabold text-foreground"
          >
            {categoryConfig.label}
          </h2>

          <img
            src="/assets/baron-wuffett/baron-wuffett-vector.svg"
            alt="Baron Wuffett"
            className="absolute right-full -top-2 -mr-2 w-24 h-24 sm:w-28 sm:h-28"
          />
        </div>

        {/* Category Description */}
        <p
          className="text-sm sm:text-base leading-relaxed animate-fade-up-delay opacity-0 text-muted-foreground"
        >
          {categoryConfig.description}
        </p>

        {/* Play Button */}
        <div className="flex items-center justify-center gap-3 animate-fade-up-delay-2 opacity-0">
          <button
            type="button"
            onClick={handlePlay}
            disabled={isLoading || leaving}
            className={cn(
              'inline-flex items-center justify-center gap-2',
              'px-8 py-3 rounded-full',
              'text-lg font-bold uppercase tracking-wide text-white',
              'shadow-lg',
              'transform transition-all duration-200 ease-out',
              'focus:outline-none focus:ring-4 focus:ring-offset-2',
              'focus:ring-foreground/50 focus:ring-offset-transparent',
              isLoading || leaving
                ? 'opacity-80 cursor-not-allowed'
                : 'hover:shadow-xl hover:scale-105 active:scale-95'
            )}
            style={{
              backgroundColor: categoryConfig.color,
            }}
            aria-label={isLoading ? `Loading ${categoryConfig.label} category` : `Play ${categoryConfig.label} category`}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                Loading...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" aria-hidden="true" />
                Play
              </>
            )}
          </button>

        </div>

        {/* Info cards */}
        <div className="space-y-2 pt-2 animate-fade-up-delay-3 opacity-0">
          <div className="flex items-center gap-3 rounded-xl px-4 py-3 text-left bg-card border border-border">
            <div className="isolate flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#f8a0a0' }}>
              <HelpCircle className="w-6 h-6 text-black" aria-hidden="true" />
            </div>
            <p className="text-sm leading-relaxed text-foreground">
              <strong>Guess the stock</strong> using price charts, financial metrics, and qualitative clues.
            </p>
          </div>

          <div className="flex items-center gap-3 rounded-xl px-4 py-3 text-left bg-card border border-border">
            <div className="isolate flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#f5d988' }}>
              <TrendingUp className="w-6 h-6 text-black" aria-hidden="true" />
            </div>
            <p className="text-sm leading-relaxed text-foreground">
              <strong>Analyze</strong> the stock with different <strong>Investment Approaches</strong> used by <strong>Wall Street</strong> legends.
            </p>
          </div>

          <div className="flex items-center gap-3 rounded-xl px-4 py-3 text-left bg-card border border-border">
            <div className="isolate flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#a8d8a8' }}>
              <Coins className="w-6 h-6 text-black" aria-hidden="true" />
            </div>
            <p className="text-sm leading-relaxed text-foreground">
              <strong>Earn Coins</strong> for correct guesses and invest them in a <strong>Paper Portfolio</strong>.
            </p>
          </div>
        </div>

        {/* Previous days replay strip */}
        <PreviousDays category={category} className="pt-4 animate-fade-up-delay-3 opacity-0" />
      </div>
    </div>
  );
};

export default GameStartOverlay;
